import React from 'react'
import { statusPill } from './theme'
import { formatSeconds, formatCurrency, formatPct } from './formatters'

/**
 * Thin always-visible status strip shown at the top of the right pane.
 *
 * Displays active project, customer, status pill, and — when a nest has been
 * generated — the key rollup totals so you can scan "what am I looking at"
 * without scrolling to the top.
 */
export default function NesterStickyHeader({
  colors,
  mode,
  project,
  nestResult,
  status,
  parts = [],
  groups = [],
  onToggleTheme,
  onRunNest,
  onSave,
  nesting,
  saving,
  activeTab,
  onTabJump,
}) {
  if (!project) {
    return (
      <div style={{
        ...styles.bar(colors),
        justifyContent: 'space-between',
      }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: colors.text }}>
          DXF Nester
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          {onSave && (
            <button onClick={onSave} disabled={saving}
              style={styles.btnPrimary(colors)} title="Create project (⌘S)">
              {saving ? 'Creating…' : 'Create project'}
            </button>
          )}
          <button onClick={onToggleTheme} style={styles.iconBtn(colors)} title={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}>
            {mode === 'dark' ? '☀' : '☾'}
          </button>
        </div>
      </div>
    )
  }

  const pill = statusPill(status || project.status || 'draft', mode)
  const totals = computeTotals(nestResult)
  const partsCount = parts.reduce((s, p) => s + (p.fill_sheet ? 0 : (p.quantity || 0)), 0)
  const placedCount = totals.parts
  const unassigned = groups.length > 0 ? parts.filter(p => !p.material_group_id).length : 0

  return (
    <div style={styles.bar(colors)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: '1 1 auto' }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 }}
          title={project.project_name}>
          {project.project_name}
        </div>
        {project.customer_name && (
          <span style={{ color: colors.textMuted, fontSize: 12, whiteSpace: 'nowrap' }} title={project.customer_name}>
            · {project.customer_name}
          </span>
        )}
        <span style={{
          ...styles.pill,
          background: pill.bg,
          color: pill.fg,
        }}>{pill.label}</span>
        {groups.length > 0 && (
          <span style={{ color: colors.textMuted, fontSize: 12 }}>
            · {groups.length} group{groups.length === 1 ? '' : 's'}
          </span>
        )}
        {parts.length > 0 && (
          <span style={{ color: colors.textMuted, fontSize: 12 }}>
            · {parts.length} part{parts.length === 1 ? '' : 's'}
            {partsCount !== parts.length && ` (qty ${partsCount})`}
          </span>
        )}
        {unassigned > 0 && (
          <span style={{
            ...styles.pill,
            background: colors.warnSoft,
            color: colors.warn,
            fontWeight: 600,
          }}>
            {unassigned} unassigned
          </span>
        )}
      </div>

      {nestResult && (
        <div style={styles.rollup(colors)}>
          <Stat colors={colors} label="Sheets" value={totals.sheets} onClick={() => onTabJump?.('overview')} />
          <Stat colors={colors} label="Time" value={formatSeconds(totals.time)} onClick={() => onTabJump?.('overview')} />
          <Stat colors={colors} label="Total" value={formatCurrency(totals.cost)} emphasis onClick={() => onTabJump?.('overview')} />
          {totals.utilization != null && (
            <Stat colors={colors} label="Util" value={formatPct(totals.utilization)} onClick={() => onTabJump?.('overview')} />
          )}
          {placedCount > 0 && (
            <Stat colors={colors} label="Placed" value={placedCount} onClick={() => onTabJump?.('overview')} />
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
        {onSave && (
          <button onClick={onSave} disabled={saving}
            style={styles.btnGhost(colors)} title="Save project (⌘S)">
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
        {onRunNest && parts.length > 0 && (
          <button onClick={onRunNest} disabled={nesting}
            style={styles.btnPrimary(colors)} title="Run nest (G)">
            {nesting ? 'Nesting…' : '▶ Nest'}
          </button>
        )}
        <button onClick={onToggleTheme} style={styles.iconBtn(colors)} title={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}>
          {mode === 'dark' ? '☀' : '☾'}
        </button>
      </div>
    </div>
  )
}

function Stat({ colors, label, value, emphasis, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
      padding: '2px 10px', border: 'none',
      background: 'transparent', cursor: onClick ? 'pointer' : 'default',
      borderLeft: `1px solid ${colors.border}`,
      color: colors.text, minWidth: 60,
    }}>
      <span style={{ fontSize: 9, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
        {label}
      </span>
      <span style={{
        fontSize: 13, fontWeight: 700, marginTop: 1,
        color: emphasis ? colors.accent : colors.text,
      }}>
        {value}
      </span>
    </button>
  )
}

function computeTotals(nestResult) {
  if (!nestResult) return { sheets: 0, time: 0, cost: 0, utilization: null, parts: 0 }
  if (nestResult.group_results && nestResult.group_results.length > 0) {
    let sheets = 0, time = 0, cost = 0, parts = 0, sumUtil = 0, utilCount = 0
    for (const g of nestResult.group_results) {
      sheets += g.sheets_needed || 0
      time += g.detailed_estimate?.total_sec || g.est_total_time_sec || 0
      cost += g.cost_summary?.total_cost || 0
      if (g.utilization_pct != null) { sumUtil += g.utilization_pct; utilCount++ }
      if (g.parts_summary) parts += g.parts_summary.reduce((s, p) => s + (p.quantity || 0), 0)
    }
    return {
      sheets, time, cost, parts,
      utilization: utilCount > 0 ? sumUtil / utilCount : null,
    }
  }
  const de = nestResult.detailed_estimate
  const cs = nestResult.cost_summary
  return {
    sheets: nestResult.sheets_needed || 0,
    time: de?.total_sec || nestResult.est_total_time_sec || 0,
    cost: cs?.total_cost || 0,
    utilization: nestResult.utilization_pct,
    parts: nestResult.parts_summary ? nestResult.parts_summary.reduce((s, p) => s + (p.quantity || 0), 0) : 0,
  }
}

const styles = {
  bar: (c) => ({
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 12px',
    background: c.panel,
    border: `1px solid ${c.border}`,
    borderRadius: 8,
    position: 'sticky', top: 0, zIndex: 20,
    boxShadow: c.shadow,
    marginBottom: 8,
    fontSize: 13,
    minHeight: 44,
  }),
  rollup: (c) => ({
    display: 'flex', alignItems: 'center',
    background: c.panelAlt,
    borderRadius: 6,
    padding: '3px 4px',
    flexShrink: 0,
  }),
  pill: {
    padding: '2px 8px', borderRadius: 10,
    fontSize: 11, fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  btnPrimary: (c) => ({
    padding: '5px 12px', borderRadius: 6,
    border: 'none', background: c.accent, color: '#fff',
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
  }),
  btnGhost: (c) => ({
    padding: '5px 10px', borderRadius: 6,
    border: `1px solid ${c.border}`,
    background: 'transparent', color: c.text,
    fontSize: 12, fontWeight: 500, cursor: 'pointer',
  }),
  iconBtn: (c) => ({
    padding: '4px 8px', borderRadius: 6,
    border: `1px solid ${c.border}`,
    background: 'transparent', color: c.text,
    fontSize: 14, cursor: 'pointer', lineHeight: 1,
  }),
}
